import { aiApi } from "api";
import { buildShareUrl, create_UUID, getTranslation } from "common";
import { useCallback, useEffect, useRef, useState } from "react";
import useTranslations from "./useTranslations";
import useSelectedOrganization from "./useSelectedOrganization";
import { useSnackbar, useUser } from "hooks";
import { CONSTANTS } from "common";

/**
 * AI Studio -- shown to the user as "AI Assistant" -- talks to
 * `POST /ai/studio/chat`, which unlike `/ai/guide` and `/ai/model` emits real
 * framed Server-Sent Events (`event: token`, `event: tool_call`,
 * `event: tool_result`, `event: title`, `event: done`, `event: error`),
 * because showing which Flowenti MCP tool ran (and what it returned) is core
 * to the feature, not an implementation detail to hide.
 *
 * The intent router adds three frames on top:
 *   `event: route`         which specialist is answering this turn
 *   `event: route_choice`  the classifier is unsure: options for the user,
 *                          then `done`, with no specialist run at all
 *   `event: artifact`      a BPMN diagram / form schema produced in chat,
 *                          which the UI offers to open in its real editor
 *
 * Implementation note: we use the browser-native `fetch()` +
 * `ReadableStream` rather than axios. XHR has no `responseType: 'stream'`
 * (that's a Node-only value -- axios' browser build would log
 * "The provided value 'stream' is not a valid enum value of type
 * XMLHttpRequestResponseType" and the connection would hang). The title
 * and session id arrive as SSE frames too, because response headers are
 * CORS-restricted in the browser and unreadable from JS.
 */

export type AiStudioRoute = "guide" | "ai_studio" | "model" | "robot";

export type AiStudioRouteOption = {
  route: AiStudioRoute;
  label: string;
  description: string;
};

/** Artifacts the `model` / `robot` specialists produce inside the chat. */
export type AiStudioArtifact =
  | { kind: "bpmn"; xml: string; filename?: string }
  | { kind: "robot_form"; schema: Record<string, unknown>; uischema: Record<string, unknown> | null }
  | { kind: "robot_code"; language: string; code: string };

/**
 * The Assistant is not answering: the request belongs to a screen that has
 * its own AI drawer (today the BPMN modeller). Rendered as a card with a
 * button; if the user takes the offer, the page navigates to `target`
 * carrying `prompt`, and that screen's assistant takes over.
 */
export type AiStudioHandoff = {
  route: AiStudioRoute;
  label: string;
  target: string;
  message: string;
  cta: string;
  prompt: string;
};

export type AiStudioPart =
  | { type: "text"; text: string }
  | { type: "tool_call"; id: string; name: string; arguments: Record<string, unknown> }
  | { type: "tool_result"; id: string; name: string; isError: boolean; content: string }
  // The classifier was not confident enough and is asking the user which
  // specialist should answer. Rendered as buttons; NEVER replayed to the
  // model as history (same rule as tool_result content).
  | { type: "route_choice"; question: string; options: AiStudioRouteOption[] }
  | { type: "artifact"; artifact: AiStudioArtifact }
  | { type: "handoff"; handoff: AiStudioHandoff };

export type AiStudioMessage = {
  uuid: string;
  isAi: boolean;
  parts: AiStudioPart[];
  isLoading: boolean;
  hidden?: boolean;
  isError?: boolean;
  prompt?: string;
};

export type AiStudioSession = {
  id: string;
  title: string;
  timestamp: number;
  messages?: AiStudioMessage[];
  idSession?: string;
  /** Sticky specialist of the conversation; undefined on pre-router sessions. */
  route?: AiStudioRoute;
};

const KNOWN_ROUTES: AiStudioRoute[] = ["guide", "ai_studio", "model", "robot"];

const asRoute = (value: unknown): AiStudioRoute | undefined =>
  typeof value === "string" && (KNOWN_ROUTES as string[]).includes(value)
    ? (value as AiStudioRoute)
    : undefined;

const extractArray = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === "object") {
    const obj = payload as Record<string, unknown>;
    for (const key of ["data", "results", "items", "sessions"]) {
      const candidate = obj[key];
      if (Array.isArray(candidate)) return candidate;
    }
  }
  return [];
};

const normalizeSession = (raw: any): AiStudioSession | null => {
  if (!raw || typeof raw !== "object") return null;
  const idValue = raw.id_session ?? raw.idSession ?? raw.id;
  const id = idValue !== undefined && idValue !== null ? String(idValue) : "";
  if (!id) return null;

  const dateSource = raw.updated_at ?? raw.created_at;
  let timestamp = Date.now();
  if (typeof dateSource === "string" || typeof dateSource === "number") {
    const parsed = new Date(dateSource).getTime();
    if (!Number.isNaN(parsed)) timestamp = parsed;
  }

  let messages: AiStudioMessage[] | undefined;
  if (Array.isArray(raw.messages)) {
    const mapped = raw.messages.flatMap((m: any, idx: number) => {
      const entry = m && typeof m === "object" ? m : {};
      const entryId = entry.id ?? idx;
      const result: AiStudioMessage[] = [];
      if (typeof entry.question === "string" && entry.question.length > 0) {
        result.push({
          uuid: `${id}-${entryId}-q`,
          isAi: false,
          parts: [{ type: "text", text: entry.question }],
          isLoading: false,
        });
      }
      if (typeof entry.answer === "string" && entry.answer.length > 0) {
        result.push({
          uuid: `${id}-${entryId}-a`,
          isAi: true,
          parts: [{ type: "text", text: entry.answer }],
          isLoading: false,
        });
      }
      return result;
    });
    messages = mapped.length > 0 ? mapped : undefined;
  }

  return {
    id,
    idSession: id,
    title: String(raw.title ?? ""),
    timestamp,
    messages,
    route: asRoute(raw.route),
  };
};

/**
 * Incrementally parse `event: <type>\ndata: <json>\n\n` frames out of a
 * growing buffer. Returns the frames found in [lastParsedLength, buffer.length)
 * and the new length to remember for the next call. Skips a trailing partial
 * frame (no closing \n\n yet) so we never half-parse JSON.
 */
const parseNewSseFrames = (
  buffer: string,
  lastParsedLength: number
): { frames: { event: string; data: any }[]; parsedLength: number } => {
  // Only re-scan from the last complete frame boundary onward.
  const unparsed = buffer.slice(lastParsedLength);
  const frames: { event: string; data: any }[] = [];

  const lastBoundary = unparsed.lastIndexOf("\n\n");
  if (lastBoundary === -1) {
    return { frames, parsedLength: lastParsedLength };
  }

  const completeChunk = unparsed.slice(0, lastBoundary);
  for (const rawFrame of completeChunk.split("\n\n")) {
    if (!rawFrame.trim()) continue;
    let eventType = "message";
    let dataLine = "";
    for (const line of rawFrame.split("\n")) {
      if (line.startsWith("event:")) eventType = line.slice(6).trim();
      else if (line.startsWith("data:")) dataLine += line.slice(5).trim();
    }
    if (!dataLine) continue;
    try {
      frames.push({ event: eventType, data: JSON.parse(dataLine) });
    } catch {
      // Malformed frame (shouldn't happen -- backend always emits valid
      // JSON) -- skip rather than throw, so one bad frame doesn't kill
      // the rest of the stream.
    }
  }

  return { frames, parsedLength: lastParsedLength + lastBoundary + 2 };
};

const useAiStudioChat = () => {
  const translations = useTranslations();
  const llmInputRef = useRef<HTMLInputElement>(null);
  const [aiAnswers, setAiAnswers] = useState<AiStudioMessage[]>([]);
  // `inflight` is true exactly when a POST /ai/studio/chat is in
  // progress. The input is NEVER disabled by this -- users can keep
  // typing their next message while the current one streams. We use
  // a separate `pendingPrompt` slot for the FIFO queue of "send as
  // soon as the current one finishes" messages, instead of disabling
  // the input.
  const [inflight, setInflight] = useState<boolean>(false);
  // Single-slot queue: if the user hits Enter while `inflight` is
  // true, the prompt is stored here and dispatched automatically
  // when the current POST settles. Single-slot (not array) so that
  // rapid re-edits while a slow response streams collapse to "send
  // the latest version" -- matches the user mental model of refining
  // the next question as the previous answer arrives.
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);
  const organization = useSelectedOrganization();
  const organizationId = organization?.id || "";
  const user = useUser();
  const { sendSnack } = useSnackbar();

  const abortControllerRef = useRef<AbortController | null>(null);
  const currentIdSessionRef = useRef<string | undefined>(undefined);
  const [currentIdSession, setCurrentIdSession] = useState<string | undefined>(undefined);
  const currentTitleRef = useRef<string | null>(null);

  const [sessions, setSessions] = useState<AiStudioSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string>("");

  // Which specialist is driving this conversation. Display state: it follows
  // the backend's `route` frames and the route stored on a reloaded session.
  const [currentRoute, setCurrentRoute] = useState<AiStudioRoute | null>(null);
  // One-shot override for the NEXT request: set when the user picks a route
  // explicitly (an option in the choice card, or the mode menu). We send it
  // once and then let the backend's stickiness take over, so `route_source`
  // in chathistory keeps telling the truth -- a permanent client-side force
  // would mark every later turn as "forced" and destroy the one signal that
  // reveals when the classifier got it wrong.
  const pendingRouteRef = useRef<AiStudioRoute | null>(null);

  const startNewSession = () => {
    const newId = create_UUID();
    setCurrentSessionId(newId);
    currentIdSessionRef.current = undefined;
    setCurrentIdSession(undefined);
    currentTitleRef.current = null;
    setCurrentRoute(null);
    pendingRouteRef.current = null;
    setAiAnswers([]);
  };

  const loadSessionsFromBackend = useCallback(async () => {
    if (!user?.userId || !organizationId) return;
    try {
      const { data } = await aiApi.get<unknown>(
        `/studio/sessions/${user.userId}/${organizationId}`
      );
      const list = extractArray(data)
        .map(normalizeSession)
        .filter((s): s is AiStudioSession => s !== null);
      setSessions(list);
    } catch (e) {
      console.error("Error loading AI Studio sessions", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId, organizationId]);

  useEffect(() => {
    if (user?.userId && organizationId) {
      setAiAnswers([]);
      setCurrentSessionId("");
      currentIdSessionRef.current = undefined;
      setCurrentIdSession(undefined);
      currentTitleRef.current = null;
      setSessions([]);
    }
    loadSessionsFromBackend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId, organizationId]);

  const loadSession = async (id: string) => {
    const session = sessions.find((s) => s.id === id);
    if (!session) return;
    pendingRouteRef.current = null;
    if (session.messages && session.messages.length > 0) {
      setCurrentSessionId(id);
      currentIdSessionRef.current = session.idSession;
      setCurrentIdSession(session.idSession);
      setCurrentRoute(session.route ?? null);
      setAiAnswers(session.messages);
      return;
    }
    if (!session.idSession || !user?.userId || !organizationId) return;
    try {
      const { data } = await aiApi.get<{ data: any }>(
        `/studio/sessions/${user.userId}/${organizationId}/${session.idSession}`
      );
      const full = data?.data;
      if (!full) return;
      const normalized = normalizeSession({ ...full, id_session: session.idSession });
      const messages = normalized?.messages ?? [];
      const route = normalized?.route;
      setSessions((prev) =>
        prev.map((s) => (s.id === id ? { ...s, messages, route } : s))
      );
      setCurrentSessionId(id);
      currentIdSessionRef.current = session.idSession;
      setCurrentIdSession(session.idSession);
      setCurrentRoute(route ?? null);
      setAiAnswers(messages);
    } catch (e) {
      console.error("Error loading AI Studio session", e);
      sendSnack({ message: "Errore durante il caricamento della chat.", type: "error" });
    }
  };

  const deleteSession = async (id: string) => {
    const session = sessions.find((s) => s.id === id);
    if (!session?.idSession || !user?.userId || !organizationId) return;
    try {
      await aiApi.delete(
        `/studio/sessions/${user.userId}/${organizationId}/${session.idSession}`
      );
    } catch (e) {
      console.error("Error deleting AI Studio session", e);
      sendSnack({ message: "Errore durante l'eliminazione della chat.", type: "error" });
      return;
    }
    setSessions((prev) => prev.filter((s) => s.id !== id));
    if (currentSessionId === id) startNewSession();
  };

  /**
   * Rename a chat from the sidebar. Same endpoint as the drawer's: the title
   * lives on every row of the session and the query does not filter by
   * `context`.
   *
   * Note the backend still regenerates the title on its own at the 1/10/20
   * message milestones (AIHistoryService.maybe_generate_title), so a manual
   * name can be overwritten later in a long conversation -- today that is
   * true of the drawer as well.
   */
  const renameSession = async (id: string, newTitle: string) => {
    const trimmed = newTitle.trim();
    if (!trimmed) return;
    const session = sessions.find((s) => s.id === id);
    if (!session?.idSession || !user?.userId || !organizationId) {
      sendSnack({
        message: "Impossibile rinominare la chat: identificativo mancante.",
        type: "error",
      });
      return;
    }
    try {
      await aiApi.patch(
        `/sessions/${user.userId}/${organizationId}/${session.idSession}/title`,
        { title: trimmed }
      );
    } catch (e) {
      console.error("Error renaming AI Studio session", e);
      sendSnack({
        message: "Errore durante la rinominazione della chat.",
        type: "error",
      });
      return;
    }
    setSessions((prev) =>
      prev.map((s) => (s.id === id ? { ...s, title: trimmed } : s))
    );
  };

  /**
   * Mint (or reuse) the public link for a chat and return it ready to paste.
   *
   * Same endpoint as the drawer's `useLLM.shareSession`: sharing lives in
   * chathistory and the backend never filters it by `context`, so an AI
   * Assistant session shares exactly like a guide one -- and the reader gets
   * `answer_clean`, i.e. the prose without this flow's tool log.
   *
   * The active conversation can be shared before it appears in `sessions`
   * (the list refreshes after the turn), hence the ref fallback: the backend
   * id exists as soon as the first `done` frame lands.
   */
  const shareSession = async (id: string): Promise<{ url: string } | null> => {
    const session = sessions.find((s) => s.id === id);
    const idSessionToShare = session?.idSession ?? currentIdSessionRef.current;
    if (!idSessionToShare || !user?.userId || !organizationId) {
      sendSnack({
        message: "Impossibile condividere la chat: identificativo mancante.",
        type: "error",
      });
      return null;
    }
    try {
      const { data } = await aiApi.post<
        | { shared?: boolean; url?: string; link?: string; shareUrl?: string }
        | string
      >(
        `/sessions/${user.userId}/${organizationId}/${idSessionToShare}/share`,
        { shared: true }
      );
      const raw =
        typeof data === "string"
          ? data
          : data?.url ?? data?.shareUrl ?? data?.link ?? "";
      const url = buildShareUrl(raw);
      if (!url) {
        sendSnack({ message: "Link non disponibile.", type: "error" });
        return null;
      }
      return { url };
    } catch (e) {
      console.error("Error sharing AI Studio session", e);
      sendSnack({
        message: "Errore durante la condivisione della chat.",
        type: "error",
      });
      return null;
    }
  };

  const stopChat = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    // Drop any queued prompt too -- Stop should kill everything pending.
    setPendingPrompt(null);
    setInflight(false);
    setAiAnswers((p) => (p.length > 0 && p[p.length - 1].isLoading ? p.slice(0, -1) : p));
  };

  /**
   * Run one POST /ai/studio/chat for `prompt` end-to-end. Returns when the
   * SSE stream settles (success, !response.ok, AbortError, transport
   * error). Does NOT consume the input element -- the caller passes the
   * prompt in explicitly so we can dispatch both user-typed prompts and
   * queue-drained prompts through the same code path.
   */
  const dispatchPrompt = async (
    prompt: string,
    opts?: {
      /** Force a specialist for this request (option click / mode menu). */
      route?: AiStudioRoute;
      /**
       * Answering a `route_choice`: replace that bubble instead of adding a
       * second copy of the user's question. The user message that produced
       * it stays on screen, and is trimmed from the history we send (the
       * prompt travels in `prompt`, so replaying it would duplicate it).
       */
      replaceUuid?: string;
    }
  ) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const forcedRoute = opts?.route ?? pendingRouteRef.current ?? undefined;
    pendingRouteRef.current = null;

    // History for THIS request. When re-answering a route_choice we cut
    // everything from the user message that started that turn onward.
    let historySource = aiAnswers;
    if (opts?.replaceUuid) {
      const choiceIdx = aiAnswers.findIndex((m) => m.uuid === opts.replaceUuid);
      if (choiceIdx >= 0) {
        let cut = choiceIdx;
        for (let i = choiceIdx - 1; i >= 0; i--) {
          if (!aiAnswers[i].isAi) {
            cut = i;
            break;
          }
        }
        historySource = aiAnswers.slice(0, cut);
      }
    }

    const aiUUID = create_UUID();
    setAiAnswers((p) =>
      opts?.replaceUuid
        ? p.map((m) =>
            m.uuid === opts.replaceUuid
              ? { uuid: aiUUID, isAi: true, parts: [], isLoading: true, prompt }
              : m
          )
        : [
            ...p,
            { uuid: create_UUID(), isAi: false, parts: [{ type: "text", text: prompt }], isLoading: false },
            { uuid: aiUUID, isAi: true, parts: [], isLoading: true, prompt },
          ]
    );
    setInflight(true);

    // The token/tool parts for the current turn -- accumulated as SSE
    // frames arrive, flushed into React state at the end of each tick so
    // we don't re-render on every byte. `parsedLength` tracks where we
    // left off in the growing text buffer, so re-scanning is O(new bytes).
    let sseBuffer = "";
    let parsedLength = 0;
    let pendingFrames: { event: string; data: any }[] = [];

    const flushFrames = () => {
      if (pendingFrames.length === 0) return;
      const frames = pendingFrames;
      pendingFrames = [];

      // Apply title-bearing frames first, since they update the sidebar
      // (outside the message list) rather than the streaming answer.
      const titleFrame = frames.find((f) => f.event === "title");
      if (titleFrame && typeof titleFrame.data?.title === "string") {
        const newTitle = titleFrame.data.title;
        currentTitleRef.current = newTitle;
        const backendId =
          typeof titleFrame.data.sessionId === "string"
            ? titleFrame.data.sessionId
            : undefined;
        if (backendId) {
          currentIdSessionRef.current = backendId;
          setCurrentIdSession(backendId);
        }
        setSessions((prev) => {
          const idx = prev.findIndex(
            (s) =>
              (backendId && s.idSession === backendId) ||
              s.id === currentSessionId
          );
          if (idx === -1) return prev;
          const next = [...prev];
          next[idx] = { ...next[idx], title: newTitle };
          return next;
        });
      }

      // The route frame updates the header chip, not the bubble -- apply it
      // outside the reducer, next to `title`.
      const routeFrame = frames.find((f) => f.event === "route");
      const announcedRoute = asRoute(routeFrame?.data?.route);
      if (announcedRoute) {
        setCurrentRoute(announcedRoute);
        setSessions((prev) =>
          prev.map((s) =>
            (currentIdSessionRef.current && s.idSession === currentIdSessionRef.current) ||
            s.id === currentSessionId
              ? { ...s, route: announcedRoute }
              : s
          )
        );
      }

      setAiAnswers((p) => {
        const list = [...p];
        const lastIdx = list.length - 1;
        if (lastIdx < 0 || list[lastIdx].uuid !== aiUUID) return list;

        const msg = { ...list[lastIdx] };
        const parts = [...msg.parts];
        // Deferred to after `list[lastIdx] = msg` below: splicing the user
        // message out while `lastIdx` is still in flight shifts the AI
        // placeholder down one slot, so the write-back would land one past
        // the end and APPEND a copy instead of replacing it -- leaving the
        // original `isLoading` bubble above the error one, spinning forever
        // (and two bubbles sharing one React key). That was the reported
        // symptom of an Ollama quota 429: "queda un spinner arriba cargando".
        let dropUserTurn = false;

        for (const frame of frames) {
          if (frame.event === "token") {
            const last = parts[parts.length - 1];
            if (last && last.type === "text") {
              parts[parts.length - 1] = { ...last, text: last.text + frame.data.text };
            } else {
              parts.push({ type: "text", text: frame.data.text });
            }
          } else if (frame.event === "tool_call") {
            // The backend always emits `id` (the OpenAI tool_call_id) so
            // we can build proper `tool` messages on the next turn. If
            // the backend is older and forgets the id, fall back to a
            // synthetic one derived from name+index -- still gives the
            // LLM a stable handle per turn, but mismatched across turns.
            parts.push({
              type: "tool_call",
              id: frame.data.id || `${frame.data.name}-${parts.length}`,
              name: frame.data.name,
              arguments: frame.data.arguments || {},
            });
          } else if (frame.event === "tool_result") {
            parts.push({
              type: "tool_result",
              id: frame.data.id || "",
              name: frame.data.name,
              isError: Boolean(frame.data.isError),
              content: frame.data.content,
            });
          } else if (frame.event === "route_choice") {
            // The backend closed the stream without running any specialist:
            // the user decides. Options are rendered as buttons by
            // RouteChoiceCard, and this part never becomes model history.
            parts.push({
              type: "route_choice",
              question: frame.data.question || "",
              options: Array.isArray(frame.data.options)
                ? frame.data.options
                    .filter((o: any) => asRoute(o?.route))
                    .map((o: any) => ({
                      route: o.route as AiStudioRoute,
                      label: String(o.label ?? o.route),
                      description: String(o.description ?? ""),
                    }))
                : [],
            });
          } else if (frame.event === "handoff") {
            parts.push({ type: "handoff", handoff: frame.data });
          } else if (frame.event === "artifact") {
            parts.push({ type: "artifact", artifact: frame.data });
          } else if (frame.event === "error") {
            // Apply the error mark immediately when the frame arrives,
            // not only in the post-loop errorFrame handler -- otherwise
            // if the error frame is fully parsed inside the reader loop
            // (the most common case for provider-rate-limit errors that
            // arrive right after the first chunk) it never reaches
            // finalFrames because parsedLength already advanced past it,
            // and the bubble is left with a partial response and no
            // spinner. This was the production symptom "texto parcial,
            // sin spinner ni error".
            msg.isError = true;
            // Clear the spinner HERE. The guard after this loop skips
            // `isLoading = false` for an errored message, so without this the
            // bubble spins forever -- the production symptom of an Ollama
            // quota 429: "queda con el spinner cargando".
            msg.isLoading = false;
            // Write into `parts`, NOT msg.parts: `parts` was snapshotted from
            // msg.parts before this loop and is assigned back onto msg right
            // after it, so anything written straight to msg.parts here gets
            // overwritten -- which is why the error text never showed up.
            parts.length = 0;
            parts.push({
              type: "text",
              text: frame.data.message || "Errore.",
            });
            // ALSO drop the user message that started this turn to
            // keep the LLM context clean for the next attempt (same
            // rationale as the post-loop handler below). Done after the
            // loop, once the bubble has been written back -- see
            // `dropUserTurn` above.
            dropUserTurn = true;
          } else if (frame.event === "done") {
            // Backstop: the backend emits `done` last on every path,
            // including the error one (ai_studio.py event_stream, finally).
            // Whatever happened above, the turn is over.
            msg.isLoading = false;
          }
          // "done" also carries sessionId, handled below.
          // "title" was applied above, outside this reducer.
        }

        msg.parts = parts;
        // The `error` and `done` branches above already cleared this,
        // along with the text they want shown. The guard is here so a later
        // flush cannot resurrect a spinner on an already-failed bubble.
        if (!msg.isError) {
          msg.isLoading = false;
        }
        list[lastIdx] = msg;
        // Only now that the AI bubble is back in place is it safe to shrink
        // the list: the error bubble simply shifts down one slot.
        if (dropUserTurn) {
          for (let i = lastIdx - 1; i >= 0; i--) {
            if (list[i].isAi) continue;
            list.splice(i, 1);
            break;
          }
        }
        return list;
      });
    };

    const baseURL = `${process.env.REACT_APP_STATS_BACKEND_BASE_URI}/ai`;
    const jwt = localStorage.getItem(CONSTANTS.STORE_JWT) || "";

    try {
      // Hard ceiling on the whole SSE request. The backend has its own
      // 30s per-LLM-call timeout, but a multi-iteration tool loop
      // (LLM -> tool -> LLM -> ...) can legitimately run 60-90s for a
      // complex query; 90s is the same budget the backend enforces
      // (see MAX_TOOL_ITERATIONS=4 and total budget in
      // service_ai_studio.py), so we align here so the frontend doesn't
      // fire its own timer on a request the backend is finishing on
      // time. The Stop button still aborts via the same controller.
      //
      // We can't use `AbortSignal.any([...])` here because the project
      // targets es5 and older TS lib versions don't expose it; instead
      // wire a manual timer that aborts controller.signal after 90s.
      // We use a plain `AbortError` (not a `TimeoutError`) so the catch
      // below treats the timeout identically to a Stop click -- a hung
      // SSE request is not the same failure as a transport-level error.
      const hardTimeoutMs = 90_000;
      const hardTimeout = setTimeout(() => {
        controller.abort(
          new DOMException("Request timed out after 90s", "AbortError")
        );
      }, hardTimeoutMs);
      try {
        const response = await fetch(`${baseURL}/studio/chat`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream",
            ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
          },
          body: JSON.stringify({
            organizationId,
            prompt,
            model: "",
            ...(forcedRoute ? { route: forcedRoute } : {}),
            chat: historySource
              .filter((m) => !m.isError)
              // Drop AI messages that never produced any text or tool parts.
              // They only exist in `aiAnswers` when an upstream provider
              // returned 200 OK with an empty stream (e.g. an invalid
              // OpenRouter JWT) -- the controller still emits `event: done`,
              // so the placeholder gets `isLoading: false` with empty parts.
              // Re-sending it as `assistant: ""` in the next turn poisons
              // the LLM context and silently breaks every subsequent turn.
              .filter((m) => {
                if (!m.isAi) return true; // keep user messages
                // Drop AI turns that failed: the controller either sent
                // `event: error` (LLM provider stream was empty / aborted)
                // or the frontend marked them isError on a transport-level
                // failure. Either way, there's no real assistant output
                // to replay -- sending "The AI provider returned no
                // response" as `assistant: "..."` to the LLM in the next
                // turn confuses it badly.
                if (m.isError) return false;
                if (m.parts.length === 0) return false;
                // has at least one text part with content, OR at least one
                // tool_call/tool_result part -- both are real assistant output.
                // route_choice and artifact parts are UI only: a bubble
                // holding just those is not an assistant answer and must not
                // be replayed (a route_choice would read as the assistant
                // asking a question it never actually asked in prose).
                return m.parts.some(
                  (p) =>
                    (p.type === "text" && p.text.length > 0) ||
                    p.type === "tool_call" ||
                    p.type === "tool_result"
                );
              })
              .map(({ isAi, parts }) => {
                // User messages are a single text bubble. The assistant
                // messages are more complex: if they contain tool_call
                // parts, OpenAI's function-calling spec requires them
                // to be replayed as ONE `assistant` message with
                // `tool_calls` (and `content` set to null when the model
                // produced no prose), followed by ONE `tool` message per
                // tool_result, in the same order as the tool_calls.
                //
                // Sending multiple shallow `{role: "assistant",
                // content: "..."}` entries interleaved with tool
                // artifacts would (a) violate the spec and (b) silently
                // cause the SDK to reject the request with a 400.
                if (!isAi) {
                  return {
                    role: "user",
                    content: parts
                      .filter(
                        (p): p is Extract<AiStudioPart, { type: "text" }> =>
                          p.type === "text"
                      )
                      .map((p) => p.text)
                      .join(""),
                  };
                }

                const toolCallParts = parts.filter(
                  (p): p is Extract<AiStudioPart, { type: "tool_call" }> =>
                    p.type === "tool_call"
                );
                const toolResultParts = parts.filter(
                  (p): p is Extract<AiStudioPart, { type: "tool_result" }> =>
                    p.type === "tool_result"
                );
                const text = parts
                  .filter(
                    (p): p is Extract<AiStudioPart, { type: "text" }> =>
                      p.type === "text"
                  )
                  .map((p) => p.text)
                  .join("");

                if (toolCallParts.length === 0 && toolResultParts.length === 0) {
                  // Pure text assistant turn. The orchestrator already
                  // filters out empty assistant messages above, so if
                  // we get here `text` is non-empty.
                  return { role: "assistant", content: text };
                }

                // The assistant emitted tool_calls. Replay as:
                //   1. one assistant message with tool_calls (content
                //      null when the model produced no prose, per spec)
                //   2. one tool message per tool_result, in order
                //
                // We use the parts themselves as the implicit source
                // of truth: the backend already emits tool_call and
                // tool_result in the order they happened, so emitting
                // tool messages in `parts` order matches the order of
                // the tool_calls that produced them.
                const messages: Array<Record<string, unknown>> = [
                  {
                    role: "assistant",
                    content: text.length > 0 ? text : null,
                    tool_calls: toolCallParts.map((tc) => ({
                      id: tc.id,
                      type: "function",
                      function: {
                        name: tc.name,
                        arguments: JSON.stringify(tc.arguments),
                      },
                    })),
                  },
                  ...toolResultParts.map((tr) => ({
                    role: "tool",
                    tool_call_id: tr.id,
                    content: tr.content,
                  })),
                ];
                return messages;
              })
              .flat(),
            ...(currentIdSessionRef.current
              ? { id_session: currentIdSessionRef.current }
              : {}),
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          // Surface non-2xx (e.g. 401 from JWTBearer, 422 from pydantic) as
          // the same error UI a transport-level failure would produce. The
          // body might be JSON or text -- try JSON first, fall back to text.
          let errMsg = `Request failed with status ${response.status}`;
          try {
            const errBody = await response.text();
            try {
              const parsed = JSON.parse(errBody);
              errMsg = parsed.detail || parsed.message || errMsg;
            } catch {
              if (errBody) errMsg = errBody;
            }
          } catch {
            // body read failed -- keep the status-only message
          }
          setInflight(false);
          abortControllerRef.current = null;
          setAiAnswers((p) => {
            const list = [...p];
            const aiIdx = list.findIndex((m) => m.uuid === aiUUID);
            if (aiIdx < 0) return list;
            list[aiIdx] = {
              ...list[aiIdx],
              isLoading: false,
              isError: true,
              parts: [{ type: "text", text: errMsg }],
            };
            // Drop the orphan user message that just failed too (see the
            // longer comment in the errorFrame handler below).
            for (let i = aiIdx - 1; i >= 0; i--) {
              if (list[i].isAi) continue;
              list.splice(i, 1);
              break;
            }
            return list;
          });
          sendSnack({ message: errMsg, type: "error" });
          return;
        }

        if (!response.body) {
          throw new Error("Response has no body (stream unsupported by browser).");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");

        // Read until the stream closes. Each chunk is a Uint8Array; decode
        // it as UTF-8 (the backend sets content-type charset) and append to
        // sseBuffer, then parse any complete \n\n-terminated frames that
        // have arrived since the last tick.
        //
        // Note: when the fetch is aborted (Stop button or the 90s hard
        // ceiling), the underlying ReadableStream rejects `reader.read()`
        // with an AbortError, NOT a clean `done: true`. Catching it here
        // keeps the loop tidy and lets the outer `try` skip the catch
        // block (which suppresses the error snackbar on Stop / timeout).
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          sseBuffer += decoder.decode(value, { stream: true });

          const { frames, parsedLength: newLen } = parseNewSseFrames(
            sseBuffer,
            parsedLength
          );
          parsedLength = newLen;
          if (frames.length > 0) {
            pendingFrames = pendingFrames.concat(frames);
            flushFrames();
          }
        }
        // Flush the decoder so any trailing bytes that arrived without a
        // final flush are appended (matters for the trailing frame).
        sseBuffer += decoder.decode();
        const { frames: finalFrames } = parseNewSseFrames(
          sseBuffer + "\n\n",
          parsedLength
        );
        if (finalFrames.length > 0) {
          pendingFrames = pendingFrames.concat(finalFrames);
          flushFrames();
        }

        setInflight(false);
        abortControllerRef.current = null;

        // The error frame (if any) was already buffered into pendingFrames
        // by the loop above -- but its effect on the answer UI is to mark
        // the message as `isError` and show the message text, which the
        // generic frame handler doesn't do. Handle it explicitly here.
        //
        // ALSO: drop the user message that preceded this AI placeholder.
        // Without this, the next submit would re-send the failed question
        // as a user message followed by another user message (because the
        // failed AI placeholder is filtered out), creating consecutive
        // user turns in the LLM context -- which the model handles poorly
        // (it treats them as one big rambling question).
        const errorFrame = (finalFrames || []).find((f) => f.event === "error");
        if (errorFrame) {
          setAiAnswers((p) => {
            const list = [...p];
            const aiIdx = list.findIndex((m) => m.uuid === aiUUID);
            if (aiIdx < 0) return list;
            list[aiIdx] = {
              ...list[aiIdx],
              isLoading: false,
              isError: true,
              parts: [
                {
                  type: "text",
                  text: errorFrame.data.message || "Errore.",
                },
              ],
            };
            // Drop the user message that just failed too -- it has no
            // valid assistant response following it, so it would poison
            // the next turn's chat history as an orphan user message.
            // Walk backwards from the AI placeholder to find the most
            // recent user message that started THIS turn.
            for (let i = aiIdx - 1; i >= 0; i--) {
              if (list[i].isAi) continue; // skip intermediate AI markers
              list.splice(i, 1);
              break;
            }
            return list;
          });
        }

        // Persist the session id the server assigned (the `done` frame also
        // carries it; if the stream was interrupted before `done`, keep
        // whatever we already had).
        const doneFrame = (finalFrames || []).find((f) => f.event === "done");
        if (doneFrame && typeof doneFrame.data?.sessionId === "string") {
          currentIdSessionRef.current = doneFrame.data.sessionId;
          setCurrentIdSession(doneFrame.data.sessionId);
        }
      } finally {
        // Always clear the hard timeout -- whether the request completed,
        // returned an error, or was aborted. Without this, an early-return
        // path (e.g. !response.ok above) leaks a 90s timer into the next
        // submit.
        clearTimeout(hardTimeout);
      }
    } catch (err: any) {
      // AbortError is the expected outcome when the user clicks "Stop"
      // OR when the hard 90s ceiling elapses. Either way, don't surface
      // it as an error snackbar -- for the user-initiated case, the Stop
      // button is the action; for the timeout case, the user already saw
      // the input stay disabled for 90s, which is enough signal.
      //
      // We DO need to mark the AI placeholder as ended: the abort can
      // happen mid-stream (between SSE frames), leaving an `isLoading`
      // bubble that would otherwise spin forever. Use a neutral message;
      // the user knows the request was cancelled.
      if (err?.name === "AbortError") {
        setInflight(false);
        abortControllerRef.current = null;
        setAiAnswers((p) => {
          const list = [...p];
          const lastIdx = list.length - 1;
          if (lastIdx >= 0 && list[lastIdx].isLoading) {
            list[lastIdx] = {
              ...list[lastIdx],
              isLoading: false,
              isError: true,
              parts: [
                {
                  type: "text",
                  text: "Richiesta annullata.",
                },
              ],
            };
          }
          return list;
        });
        return;
      }
      setInflight(false);
      abortControllerRef.current = null;
      setAiAnswers((p) => {
        const list = [...p];
        const lastIdx = list.length - 1;
        if (lastIdx >= 0 && list[lastIdx].isLoading) {
          list[lastIdx] = {
            ...list[lastIdx],
            isLoading: false,
            isError: true,
            parts: [
              {
                type: "text",
                text: "Si è verificato un errore durante la generazione della risposta. Riprova.",
              },
            ],
          };
        }
        return list;
      });
      sendSnack({
        message: "Errore di connessione al provider AI.",
        type: "error",
      });
    }
  };

  // Keep the in-memory session list in sync with the active conversation.
  useEffect(() => {
    if (!currentSessionId || aiAnswers.length === 0) return;
    setSessions((prev) => {
      const existingIdx = prev.findIndex((s) => s.id === currentSessionId);
      const defaultTitle = getTranslation(translations, "ai_studio.new_chat_default_title") || "New chat";
      const title = currentTitleRef.current || (existingIdx !== -1 ? prev[existingIdx].title : "") || defaultTitle;
      const updated: AiStudioSession = {
        id: currentSessionId,
        title,
        timestamp: Date.now(),
        messages: aiAnswers,
        idSession: currentIdSessionRef.current,
        route: currentRoute ?? undefined,
      };
      const next = [...prev];
      if (existingIdx !== -1) next[existingIdx] = { ...next[existingIdx], ...updated };
      else next.push(updated);
      return next;
    });
  }, [aiAnswers, currentSessionId, currentRoute, translations]);

  /**
   * Submit handler. If `inflight` is false, dispatch the prompt
   * immediately. If `inflight` is true, enqueue it in `pendingPrompt`
   * (single-slot: the latest prompt wins) and let the current dispatch
   * pick it up in its finally block. This is the ChatGPT-style flow
   * where the input is always free and rapid re-edits collapse to
   * "send the latest version" without ever disabling the input.
   */
  const handleSubmit = (opts?: { route?: AiStudioRoute }) => {
    const prompt = llmInputRef.current?.value || "";
    if (!prompt) return;
    if (llmInputRef.current) llmInputRef.current.value = "";

    if (opts?.route) pendingRouteRef.current = opts.route;

    if (inflight) {
      // Replace any previous queued prompt -- "el último gana".
      setPendingPrompt(prompt);
      return;
    }
    void dispatchPrompt(prompt, { route: opts?.route });
  };

  /**
   * The user picked one of the options of a `route_choice` bubble. Re-send
   * the SAME prompt (kept on the bubble as `msg.prompt`) with the chosen
   * specialist forced, replacing the choice card with the real answer.
   */
  const chooseRoute = (messageUuid: string, route: AiStudioRoute) => {
    const message = aiAnswers.find((m) => m.uuid === messageUuid);
    const prompt = message?.prompt;
    if (!prompt) return;
    setCurrentRoute(route);
    void dispatchPrompt(prompt, { route, replaceUuid: messageUuid });
  };

  /**
   * Manual mode change from the header chip. Takes effect on the NEXT
   * message: switching specialist mid-answer would leave the running turn
   * mislabelled.
   */
  const setRoute = (route: AiStudioRoute) => {
    pendingRouteRef.current = route;
    setCurrentRoute(route);
  };

  // Whenever `inflight` flips false and a `pendingPrompt` is waiting,
  // dispatch it. This is the FIFO link: the dispatchPrompt function
  // is the only writer of `inflight`, so the chain `dispatch ->
  // dispatch.setInflight(false) -> effect -> dispatch` reliably
  // processes the queue without losing prompts.
  useEffect(() => {
    if (inflight || pendingPrompt === null) return;
    const next = pendingPrompt;
    setPendingPrompt(null);
    void dispatchPrompt(next);
    // We intentionally exclude `dispatchPrompt` from deps: it captures
    // the latest closures (setAiAnswers, etc.) and is recreated on
    // every render. Adding it would cause an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inflight, pendingPrompt]);

  return {
    llmInputRef,
    aiAnswers,
    inflight,
    pendingPrompt,
    stopChat,
    sessions,
    currentSessionId,
    currentIdSession,
    startNewSession,
    loadSession,
    deleteSession,
    renameSession,
    shareSession,
    refreshSessions: loadSessionsFromBackend,
    handleSubmit,
    currentRoute,
    setRoute,
    chooseRoute,
  };
};

export default useAiStudioChat;
